/**
 * AuthContext — Cognito authentication state management.
 *
 * Cognito standard attributes used:
 *   given_name  → First Name
 *   family_name → Last Name
 *   name        → Preferred Name (what the app calls "displayName")
 *
 * If Preferred Name is blank at signup, it defaults to First Name.
 *
 * Supports FORCE_CHANGE_PASSWORD flow for manually-created member accounts:
 *   Admin creates user with temp password via AdminCreateUser →
 *   Member signs in → hits NEW_PASSWORD_REQUIRED challenge →
 *   Member sets new password via completeNewPassword() →
 *   Session established normally.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
});

export interface AuthUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Preferred name — what we display throughout the app */
  displayName: string;
  householdId: string | null;
  role: string | null;
  isAdmin: boolean;
  /** True when custom:excludeFromAnalytics Cognito attribute is 'true' */
  excludeFromAnalytics: boolean;
}

interface SignUpFields {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** Optional — defaults to firstName if blank */
  preferredName?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when signIn hits FORCE_CHANGE_PASSWORD — show password change form */
  newPasswordRequired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Complete the FORCE_CHANGE_PASSWORD challenge after signIn */
  completeNewPassword: (newPassword: string) => Promise<void>;
  signUp: (fields: SignUpFields) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  /** Request a password reset code be sent to the user's email */
  forgotPassword: (email: string) => Promise<void>;
  /** Confirm a password reset with the verification code and new password */
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  getToken: () => Promise<string>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  // Stored for completeNewPassword — CognitoUser instance during FORCE_CHANGE_PASSWORD
  const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null);

  const extractUser = (session: CognitoUserSession): AuthUser => {
    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();

    const firstName = payload.given_name || '';
    const lastName = payload.family_name || '';
    // Preferred name (Cognito "name") falls back to firstName, then email
    const displayName = payload.name || firstName || payload.email || 'User';

    return {
      userId: payload.sub,
      email: payload.email || '',
      firstName,
      lastName,
      displayName,
      householdId: payload['custom:householdId'] || null,
      role: payload['custom:role'] || null,
      isAdmin: (() => {
        const groups = payload['cognito:groups'];
        if (Array.isArray(groups)) return groups.includes('tabletryb-admins');
        if (typeof groups === 'string') return groups.split(/[,\s]+/).includes('tabletryb-admins');
        return false;
      })(),
      excludeFromAnalytics: payload['custom:excludeFromAnalytics'] === 'true',
    };
  };

  // Check for existing session on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session?.isValid()) {
          setUser(extractUser(session));
        }
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          setUser(extractUser(session));
          setNewPasswordRequired(false);
          setPendingCognitoUser(null);
          resolve();
        },
        onFailure: (err) => {
          setNewPasswordRequired(false);
          setPendingCognitoUser(null);
          reject(err);
        },
        newPasswordRequired: () => {
          // Store the CognitoUser so completeNewPassword can use it
          setPendingCognitoUser(cognitoUser);
          setNewPasswordRequired(true);
          resolve(); // Resolve (not reject) — the UI will show the password change form
        },
      });
    });
  }, []);

  /**
   * Complete the FORCE_CHANGE_PASSWORD challenge.
   * Called after signIn sets newPasswordRequired = true.
   */
  const completeNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!pendingCognitoUser) {
        return reject(new Error('No pending password challenge'));
      }

      pendingCognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          setUser(extractUser(session));
          setNewPasswordRequired(false);
          setPendingCognitoUser(null);
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }, [pendingCognitoUser]);

  const signUp = useCallback(async (fields: SignUpFields): Promise<void> => {
    const { email, password, firstName, lastName, preferredName } = fields;
    // If no preferred name provided, default to first name
    const displayName = preferredName?.trim() || firstName;

    return new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'name', Value: displayName }),
      ];

      userPool.signUp(email, password, attributes, [], (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }, []);

  /**
   * Request a password reset. Cognito sends a one-time code to the
   * user's email. The promise resolves once the code has been dispatched;
   * the UI should then prompt for the code + new password and call
   * confirmPassword(). Both `onSuccess` and `inputVerificationCode`
   * resolve here — different Cognito user pool configurations fire
   * different callbacks for the "code sent" success case.
   */
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
        inputVerificationCode: () => resolve(),
      });
    });
  }, []);

  /**
   * Complete a password reset. Submits the verification code from
   * forgotPassword() together with the user's new password. On success
   * the user is NOT signed in — the caller must invoke signIn()
   * separately with the new password.
   */
  const confirmPassword = useCallback(async (email: string, code: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const signOut = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setUser(null);
    setNewPasswordRequired(false);
    setPendingCognitoUser(null);
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) return reject(new Error('Not authenticated'));

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) return reject(err || new Error('Invalid session'));
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, []);

  /**
   * Refresh user session to pick up server-side Cognito attribute changes.
   * Uses refreshSession() with the refresh token — NOT getSession() which
   * returns the cached JWT (would miss server-side attribute updates).
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) return reject(new Error('Not authenticated'));

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) return reject(err || new Error('No session'));

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (refreshErr: Error | null, newSession: CognitoUserSession) => {
          if (refreshErr) {
            console.error('Failed to refresh session:', refreshErr);
            return reject(refreshErr);
          }
          setUser(extractUser(newSession));
          resolve();
        });
      });
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      newPasswordRequired,
      signIn,
      completeNewPassword,
      signUp,
      confirmSignUp,
      forgotPassword,
      confirmPassword,
      signOut,
      getToken,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
